"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, UtensilsCrossed, Wine, Star, Sparkles, ShoppingBag, Package, Award } from "lucide-react";
export default function TheEuropeanPantry() {
  return <section data-section-id="256772" id="the-european-pantry" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--primary)_0%,transparent_30%)] opacity-15 pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-3xl blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Main Text Content */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-lg bg-card border border-border/40 text-primary shadow-xs">
                  <UtensilsCrossed className="w-4 h-4 text-primary" />
                  <span className="text-xs font-default font-semibold tracking-widest uppercase text-primary">
                    The Market
                  </span>
                  <Wine className="w-4 h-4 text-primary" />
                </div>

                {/* Relocated Artisanal Selection Callout */}
                <div className="inline-flex items-center gap-3 px-3.5 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                  <div className="bg-primary/20 p-1 rounded-md text-primary">
                    <Star className="w-3.5 h-3.5 fill-primary" />
                  </div>
                  <div className="text-left">
                    <p className="font-default font-bold text-xs uppercase tracking-wider text-primary">
                      Artisanal Selection
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-none">
                      Enfield, Connecticut
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold leading-tight text-foreground">
                The European <br />
                <span className="text-primary">Pantry Staples</span>
              </h2>
            </div>

            <div className="space-y-6 max-w-2xl">
              <p className="text-xl md:text-2xl font-serif italic text-muted-foreground leading-relaxed">
                "Discover a curated collection of Europe's finest flavors. From hand-pressed oils to artisanal pastas, we bring the heritage of the continent to your kitchen."
              </p>
              <p className="text-base text-muted-foreground/80 leading-relaxed font-default">
                Our market shelves are stocked with specialty chocolates, seasonal delicacies, and authentic ingredients sourced directly from small-batch producers. Elevate your home cooking with the same quality we use in our café.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Button asChild size="lg" className="rounded-md px-8 bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm">
                <Link to="/MarketPage">
                  Shop the Market
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md px-8 border-primary/30 text-foreground hover:bg-primary/10 shadow-sm">
                <Link to="/VisitUsPage">
                  Visit Our Café
                </Link>
              </Button>
            </div>
          </div>

          {/* Decorative Icon & Typography Color-Block Panel */}
          <div className="lg:col-span-5 relative">
            <div className="bg-card/70 backdrop-blur-sm border border-border/30 rounded-2xl p-8 lg:p-10 shadow-xl relative overflow-hidden space-y-6">
              <div className="absolute top-0 right-0 w-36 h-36 bg-primary/10 rounded-bl-3xl pointer-events-none" />

              <div className="flex items-center justify-between pb-6 border-b border-border/30">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-primary/15 border border-primary/25 text-primary">
                    <ShoppingBag className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Direct Import</span>
                    <h3 className="text-lg font-default font-semibold text-foreground">Market Provisions</h3>
                  </div>
                </div>
                <Sparkles className="w-5 h-5 text-primary/70" />
              </div>

              {/* Icon Highlight Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-background/50 border border-border/20 space-y-2">
                  <div className="w-8 h-8 rounded-md bg-primary/15 flex items-center justify-center text-primary">
                    <Package className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground">Small-Batch Goods</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">Direct from family-run regional producers</p>
                </div>

                <div className="p-4 rounded-lg bg-background/50 border border-border/20 space-y-2">
                  <div className="w-8 h-8 rounded-md bg-primary/15 flex items-center justify-center text-primary">
                    <Wine className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground">Curated Specialties</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">Pressed oils, vinegars, and artisanal preserves</p>
                </div>

                <div className="p-4 rounded-lg bg-background/50 border border-border/20 space-y-2">
                  <div className="w-8 h-8 rounded-md bg-primary/15 flex items-center justify-center text-primary">
                    <UtensilsCrossed className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground">Café Quality</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">The same pure ingredients used in our kitchen</p>
                </div>

                <div className="p-4 rounded-lg bg-background/50 border border-border/20 space-y-2">
                  <div className="w-8 h-8 rounded-md bg-primary/15 flex items-center justify-center text-primary">
                    <Award className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground">Old-World Tradition</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">Time-tested European regional standards</p>
                </div>
              </div>

              {/* Decorative Quote / Note */}
              <div className="pt-2">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-3">
                  <Star className="w-5 h-5 text-primary shrink-0 fill-primary/30" />
                  <p className="text-xs font-serif italic text-foreground/90">
                    Hand-selected weekly by our market staff in Enfield, Connecticut.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}